"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { addMonths, getWeekStart, hasOpenSlot, isDateAvailable, isSameDay, startOfDay, type Availability } from "./booking-data";
import { ArrowLeftIcon, ArrowRightIcon } from "./booking-icons";
type CalendarCell = {
  date: Date;
  isCurrentMonth: boolean;
};
type Props = {
  locale: string;
  isArabic: boolean;
  month: Date;
  selected: Date | null;
  today: Date;
  onMonthChange: (month: Date) => void;
  onSelect: (date: Date) => void;
  /** Null until the availability endpoint answers; the grid stays optimistic. */
  availability: Availability | null;
};
function buildCells(month: Date, weekStart: number): CalendarCell[] {
  const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
  const leading = (firstOfMonth.getDay() - weekStart + 7) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const totalCells = Math.ceil((leading + daysInMonth) / 7) * 7;
  return Array.from({
    length: totalCells
  }, (_, index) => {
    const date = new Date(month.getFullYear(), month.getMonth(), index - leading + 1);
    return {
      date,
      isCurrentMonth: date.getMonth() === month.getMonth()
    };
  });
}
export default function BookingCalendar(t0) {
  const $ = _c(67);
  const {
    locale,
    isArabic,
    month,
    selected,
    today,
    onMonthChange,
    onSelect,
    availability
  } = t0;
  const t = useTranslations("Booking");
  const intlLocale = isArabic ? "ar-u-nu-latn" : locale;
  const weekStart = getWeekStart(locale);
  let t1;
  if ($[0] !== intlLocale || $[1] !== month) {
    t1 = new Intl.DateTimeFormat(intlLocale, {
      month: "long",
      year: "numeric"
    }).format(month);
    $[0] = intlLocale;
    $[1] = month;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const monthLabel = t1;
  let t2;
  if ($[3] !== intlLocale || $[4] !== weekStart) {
    const formatter = new Intl.DateTimeFormat(intlLocale, {
      weekday: "short"
    });
    t2 = Array.from({
      length: 7
    }, (_, index) => formatter.format(new Date(2024, 0, 7 + (weekStart + index) % 7)));
    $[3] = intlLocale;
    $[4] = weekStart;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const weekdayLabels = t2;
  let t3;
  if ($[6] !== month || $[7] !== weekStart) {
    t3 = buildCells(month, weekStart);
    $[6] = month;
    $[7] = weekStart;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const cells = t3;
  let t4;
  if ($[9] !== month) {
    t4 = startOfDay(new Date(month.getFullYear(), month.getMonth(), 1));
    $[9] = month;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const monthStart = t4;
  const canGoBack = monthStart > startOfDay(today);
  const PreviousIcon = isArabic ? ArrowRightIcon : ArrowLeftIcon;
  const NextIcon = isArabic ? ArrowLeftIcon : ArrowRightIcon;
  let t5;
  if ($[11] !== month || $[12] !== onMonthChange) {
    t5 = () => onMonthChange(addMonths(month, -1));
    $[11] = month;
    $[12] = onMonthChange;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = !canGoBack;
  let t7;
  if ($[14] !== t) {
    t7 = t("previousMonth");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== PreviousIcon) {
    t8 = <PreviousIcon className="size-4" />;
    $[16] = PreviousIcon;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t9 = <button type="button" onClick={t5} disabled={t6} aria-label={t7} className="flex size-8 items-center justify-center rounded-full border border-white/16 text-white/80 transition-colors duration-200 hover:border-cyan-500/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-white/16 disabled:hover:text-white/80">{t8}</button>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== monthLabel) {
    t10 = <span className="text-[18px] font-bold text-white">{monthLabel}</span>;
    $[23] = monthLabel;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== month || $[26] !== onMonthChange) {
    t11 = () => onMonthChange(addMonths(month, 1));
    $[25] = month;
    $[26] = onMonthChange;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t) {
    t12 = t("nextMonth");
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== NextIcon) {
    t13 = <NextIcon className="size-4" />;
    $[30] = NextIcon;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    t14 = <button type="button" onClick={t11} aria-label={t12} className="flex size-8 items-center justify-center rounded-full border border-white/16 text-white/80 transition-colors duration-200 hover:border-cyan-500/60 hover:text-white">{t13}</button>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t10 || $[37] !== t14 || $[38] !== t9) {
    t15 = <div className="flex items-center justify-between gap-3">{t9}{t10}{t14}</div>;
    $[36] = t10;
    $[37] = t14;
    $[38] = t9;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== weekdayLabels) {
    t16 = <div className="grid grid-cols-7 gap-x-1">{weekdayLabels.map(_temp)}</div>;
    $[40] = weekdayLabels;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== availability || $[43] !== cells || $[44] !== onSelect || $[45] !== selected || $[46] !== today) {
    let t18;
    if ($[48] !== availability || $[49] !== onSelect || $[50] !== selected || $[51] !== today) {
      t18 = t19 => {
        const {
          date,
          isCurrentMonth
        } = t19;
        const available = isCurrentMonth && isDateAvailable(date, today) && hasOpenSlot(date, availability);
        const isSelected = isSameDay(date, selected);
        const isToday = isSameDay(date, today);
        return <button key={date.toISOString()} type="button" disabled={!available} aria-pressed={isSelected} onClick={() => onSelect(date)} className={`mx-auto flex h-8 w-full max-w-11 items-center justify-center rounded-full text-[16px] transition-colors duration-200 ${isSelected ? "bg-[#0CA5F0] font-bold text-white shadow-[0_6px_18px_rgba(12,165,240,0.35)]" : isToday && available ? "bg-white/16 font-semibold text-white" : available ? "text-white/90 hover:bg-white/8 hover:text-white" : "cursor-not-allowed text-white/22"}`}>{date.getDate()}</button>;
      };
      $[48] = availability;
      $[49] = onSelect;
      $[50] = selected;
      $[51] = today;
      $[52] = t18;
    } else {
      t18 = $[52];
    }
    t17 = cells.map(t18);
    $[42] = availability;
    $[43] = cells;
    $[44] = onSelect;
    $[45] = selected;
    $[46] = today;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[53] !== t17) {
    t18 = <div className="grid grid-cols-7 gap-x-1 gap-y-3.5">{t17}</div>;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t16 || $[56] !== t18) {
    t19 = <div className="flex flex-col gap-4">{t16}{t18}</div>;
    $[55] = t16;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span aria-hidden="true" className="size-2.5 rounded-full bg-white/28" />;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t) {
    t21 = t("calendarNote");
    $[59] = t;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== t21) {
    t22 = <p className="m-0 flex items-center gap-2.5 text-[14px] text-white/44">{t20}{t21}</p>;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t15 || $[64] !== t19 || $[65] !== t22) {
    t23 = <div className="flex flex-col gap-6 rounded-2xl border border-white/8 bg-[#070B14] p-5 sm:p-6">{t15}{t19}{t22}</div>;
    $[63] = t15;
    $[64] = t19;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  return t23;
}
function _temp(label) {
  return <span key={label} className="text-center text-[11px] font-medium text-white/44 md:text-[14px]">{label}</span>;
}